
import { useAppSelector } from '@/app/hooks';
import { normalizeRoles, hasPermission as canonicalHasPermission } from '@/rbac/rbac.generated';

export const useRoleAccess = () => {
  const user = useAppSelector(state => state.auth.user);
  const permissions = useAppSelector(state => state.auth.permissions || []);

  // main-app is the self-service surface for the company (admins manage their
  // own org here under RBAC Model A; platform operators live in admin-portal).
  // Roles are additive — read the whole array and normalize aliases so the
  // canonical tenant roles (tenant_owner / hr_manager / hr_staff / manager /
  // employee) are recognised, not just the legacy names.
  const rawRoles: string[] =
    (Array.isArray(user?.roles) && user!.roles!.length
      ? (user!.roles as unknown as string[])
      : user?.role
        ? [user.role as unknown as string]
        : []);
  const canonicalRoles = normalizeRoles(rawRoles);
  const has = (role: string) => canonicalRoles.includes(role);

  // Map to the binary nav tier ('manager' surfaces the team/approvals nav).
  // tenant_owner + hr_manager manage teams, so they get the manager view; the
  // finer HR/owner capabilities are exposed via the flags + permissions below.
  const userRole =
    has('manager') || has('hr_manager') || has('tenant_owner') ? 'manager' : 'employee';

  // Phase 4 (authz-hardening): ONE truth for "do I hold this permission" —
  // the server-resolved set (custom roles included) UNIONED with the static
  // role map (so an empty/stale Redux set never denies a built-in grant).
  // Both sources use CANONICAL permission keys only.
  const explicitPerms = new Set(
    (permissions as unknown[]).map((p) => (typeof p === 'string' ? p : '')).filter(Boolean),
  );
  const holds = (permission: string) =>
    canonicalHasPermission(canonicalRoles, permission) ||
    explicitPerms.has(permission) ||
    explicitPerms.has('*');

  const hasPermission = (permission: string) => holds(permission);

  const hasAnyPermission = (requiredPermissions: string[]) =>
    requiredPermissions.some(holds);

  const hasAllPermissions = (requiredPermissions: string[]) =>
    requiredPermissions.every(holds);

  // Legacy page gate — now speaks CANONICAL keys (the old 'view_dashboard'
  // vocabulary existed nowhere in the RBAC catalog, so every check was
  // vacuously fail-open). Unknown pages stay allowed: route-level guards in
  // TenantLayout are the enforcement point; this is a coarse nav helper.
  const canAccessPage = (pageName: string) => {
    const pagePermissions: Record<string, string[]> = {
      'employees': ['employee:read'],
      'reports': ['reports:view'],
    };
    const requiredPermissions = pagePermissions[pageName] || [];
    return requiredPermissions.length === 0 || hasAnyPermission(requiredPermissions);
  };

  // Canonical tenant roles.
  const isTenantOwner = has('tenant_owner');
  const isHrManager = has('hr_manager');
  const isHrStaff = has('hr_staff');
  // Finance: reviews/approves payroll, read-only on employees. Not an HR/team role.
  const isFinance = has('finance');
  // "Manager view" = anyone who oversees a team / approves leave.
  const isManager = has('manager') || isHrManager || isTenantOwner;
  // Company admin = the tenant owner (founder). Keep the name for back-compat.
  const isTenantAdmin = isTenantOwner;
  const isSystemAdmin = canonicalRoles.some((r) => r.startsWith('platform_'));
  // A pure employee is someone with none of the above elevated roles.
  const isEmployee = !isManager && !isHrStaff && !isTenantAdmin && !isFinance;

  // Capability flags driven by the canonical permission model (so e.g. hr_staff,
  // who can edit employees but isn't a "manager", is handled correctly).
  // Phase 4: capability flags now honor the server-resolved set too, so a
  // dynamic custom role granting e.g. payroll:approve lights these up.
  const canManageEmployees = holds('employee:write');
  const canApproveLeave = holds('leave:approve');
  // Payroll capabilities (drive the Payroll nav + approval surfaces).
  const canViewPayroll = holds('payroll:read');
  const canRunPayroll = holds('payroll:write');
  const canApprovePayroll = holds('payroll:approve');
  const canAdminister = holds('user:read:own-tenant')
    || holds('department:write')
    || holds('leavepolicy:write')
    || holds('tenant:update:own');

  return {
    userRole,
    canonicalRoles,
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessPage,
    isManager,
    isEmployee,
    isTenantAdmin,
    isSystemAdmin,
    isTenantOwner,
    isHrManager,
    isHrStaff,
    isFinance,
    canManageEmployees,
    canApproveLeave,
    canViewPayroll,
    canRunPayroll,
    canApprovePayroll,
    canAdminister,
  };
};
